import React from 'react';
import {Button, Table, Tag} from 'antd';
import type {TableColumnsType} from 'antd';
import {useTranslation} from 'react-i18next';
import {formatEpochToDateTime} from '../../../../utils/dateTimeUtils';
import {usePickupFailureReasonText} from '../hooks/usePickupFailureReasonText';
import {
    PopReviewScene,
    PopReviewStatus,
    PopReviewType,
    getPopPickupResult,
    isPopReviewFinished
} from '../graphql/types/PopReviewType';

interface PopReviewTableProps {
    popReviewData: PopReviewType[];
    loading: boolean;
    /** 打开详情 Drawer（批量审核也由此入口逐条推进） */
    showDetail: (record: PopReviewType) => void;
    onSelectChange: (selectedRowKeys: React.Key[], selectedRows: PopReviewType[]) => void;
    selectedRowKeys: React.Key[];
    /** Hub ID → 名称；未命中时兜底显示 ID */
    hubNameById: Record<string, string>;
    /** 站点 ID → 名称；未命中回落显示 ID */
    pickupSiteNameById: Record<string, string>;
    /** 司机 account ID → 姓名；未命中回落显示 ID */
    pickupDriverNameById: Record<string, string>;
    /** 不合格原因码 → 当前语言文案（当前场景的字典）；未命中回落显示码 */
    reasonTextByCode: Record<string, string>;
    /** 当前场景；揽收失败才有「揽收失败原因」列 */
    scene: PopReviewScene;
    /** 是否显示审核结果类列（不合格原因 / 审核员 / 审核时间），仅「已审核」Tab 为 true */
    showReviewColumns: boolean;
    /**
     * 是否显示批量选择复选框。已审核单不可再审核，故「已审核」Tab 不需要选择。
     * 注意：操作列（查看/审核）始终显示，与本开关无关。
     */
    showRowSelection?: boolean;
}

/** 状态对应的 Tag 颜色：待审核中性、合格绿、不合格红、已取消灰 */
export const STATUS_COLOR: Record<PopReviewStatus, string> = {
    [PopReviewStatus.PENDING_REVIEW]: 'processing',
    [PopReviewStatus.APPROVED]: 'success',
    [PopReviewStatus.REJECTED]: 'error',
    [PopReviewStatus.CANCELLED]: 'default'
};

const PopReviewTable: React.FC<PopReviewTableProps> = ({
    popReviewData,
    loading,
    showDetail,
    onSelectChange,
    selectedRowKeys,
    hubNameById,
    pickupSiteNameById,
    pickupDriverNameById,
    reasonTextByCode,
    scene,
    showReviewColumns,
    showRowSelection = true
}) => {
    const {t} = useTranslation();
    const getPickupFailureReasonText = usePickupFailureReasonText();

    /** 审核结论文案：后端为引擎态，展示语义在前端转换（APPROVED→合格，REJECTED→不合格） */
    const getStatusText = (status?: PopReviewStatus) =>
        status ? t(`popReview.enum.status.${status}`) : t('popReview.enum.status.UNKNOWN');

    const columns: TableColumnsType<PopReviewType> = [
        {
            title: t('popReview.table.appointmentNumber'),
            dataIndex: 'appointmentNumber',
            key: 'appointmentNumber',
            render: (text?: string, record?: PopReviewType) => (
                <Button type="link" style={{padding: 0}} onClick={() => record && showDetail(record)}>
                    {text || record?.reviewTaskId || '-'}
                </Button>
            )
        },
        {
            title: t('popReview.table.status'),
            dataIndex: 'status',
            key: 'status',
            render: (status?: PopReviewStatus) => (
                <Tag color={status ? STATUS_COLOR[status] : 'default'}>{getStatusText(status)}</Tag>
            )
        },
        {
            title: t('popReview.table.pickupHub'),
            dataIndex: 'pickupHubId',
            key: 'pickupHubId',
            // 优先用后端 DataLoader 回查的名称；其次筛选区下拉映射；都没有才回落 ID
            render: (hubId?: string, record?: PopReviewType) =>
                record?.pickupHubName || (hubId ? hubNameById[hubId] || hubId : '-')
        },
        {
            title: t('popReview.table.pickupSiteId'),
            dataIndex: 'pickupSiteId',
            key: 'pickupSiteId',
            render: (siteId?: string) => siteId || '-'
        },
        {
            title: t('popReview.table.pickupSite'),
            key: 'pickupSiteName',
            // 优先用揽收侧回查的名称（不依赖是否选过 Hub）；其次下拉映射；都没有回落 ID
            render: (_: unknown, record: PopReviewType) =>
                record.pickupDetail?.siteName ||
                (record.pickupSiteId
                    ? pickupSiteNameById[record.pickupSiteId] || record.pickupSiteId
                    : '-')
        },
        {
            title: t('popReview.table.driver'),
            key: 'driver',
            // 三级兜底：后端 DataLoader 回查的姓名 → 筛选区下拉映射 → 回落显示 ID。
            // 下拉只覆盖当前 Hub 下的在职司机，故不能只依赖它
            render: (_: unknown, record: PopReviewType) =>
                record.driverName ||
                (record.driverId
                    ? pickupDriverNameById[record.driverId] || record.driverId
                    : '-')
        },
        {
            // PRD 列顺序：揽收状态紧跟揽收司机
            title: t('popReview.table.pickupStatus'),
            key: 'pickupStatus',
            render: (_: unknown, record: PopReviewType) => {
                const result = getPopPickupResult(
                    record.reviewScene,
                    record.pickupDetail?.isPartialPickup
                );
                return result ? t(`popReview.enum.pickupResult.${result}`) : '-';
            }
        },
        ...(scene === PopReviewScene.PICKUP_FAILED
            ? [
                  {
                      title: t('popReview.table.pickupFailureReason'),
                      key: 'pickupFailureReason',
                      render: (_: unknown, record: PopReviewType) =>
                          record.pickupFailureReasonCode
                              ? getPickupFailureReasonText(record.pickupFailureReasonCode)
                              : '-'
                  }
              ]
            : []),
        {
            title: t('popReview.table.contactPhone'),
            key: 'contactPhone',
            render: (_: unknown, record: PopReviewType) => record.pickupDetail?.contactPhone || '-'
        },
        {
            title: t('popReview.table.channelName'),
            key: 'channelName',
            // 名称由后端 DataLoader 回查 channel；查不到回落显示 ID
            render: (_: unknown, record: PopReviewType) =>
                record.pickupDetail?.channelName || record.pickupDetail?.channelId || '-'
        },
        {
            title: t('popReview.table.address'),
            key: 'address',
            // 地址是唯一可能很长的列，保留截断并给出上限，其余列按内容自适应
            ellipsis: true,
            width: 240,
            render: (_: unknown, record: PopReviewType) => record.pickupDetail?.address || '-'
        },
        {
            title: t('popReview.table.source'),
            key: 'source',
            // 后端返原始枚举值，文案在前端映射（缺省显示原值，便于发现未收录的新枚举）
            render: (_: unknown, record: PopReviewType) =>
                record.pickupDetail?.source
                    ? t(`popReview.enum.source.${record.pickupDetail.source}`, {
                          defaultValue: record.pickupDetail.source
                      })
                    : '-'
        },
        {
            title: t('popReview.table.isNewWarehouse'),
            key: 'isNewWarehouse',
            render: (_: unknown, record: PopReviewType) =>
                record.pickupDetail?.isNewWarehouse == null
                    ? '-'
                    : t(record.pickupDetail.isNewWarehouse === 1 ? 'common.yes' : 'common.no')
        },
        {
            title: t('popReview.table.routeName'),
            key: 'routeName',
            render: (_: unknown, record: PopReviewType) => record.pickupDetail?.routeName || '-'
        },
        {
            title: t('popReview.table.zipcode'),
            key: 'zipcode',
            render: (_: unknown, record: PopReviewType) => record.pickupDetail?.zipcode || '-'
        },
        {
            title: t('popReview.table.expectedPickupTime'),
            key: 'expectedPickupTime',
            // 日期与开始时间分列存放，展示时拼成一个可读时间
            render: (_: unknown, record: PopReviewType) => {
                const date = record.pickupDetail?.expectedPickupDate;
                const time = record.pickupDetail?.expectedPickupTimeStart;
                if (!date) return '-';
                return time ? `${date} ${time.slice(0, 5)}` : date;
            }
        },
        {
            title: t('popReview.table.timezone'),
            key: 'timezone',
            render: (_: unknown, record: PopReviewType) => record.pickupDetail?.timezone || '-'
        },
        {
            title: t('popReview.table.actualParcelCount'),
            dataIndex: 'actualParcelCount',
            key: 'actualParcelCount',
            render: (count?: number) => (count || '-')
        },
        {
            title: t('popReview.table.taskNumber'),
            key: 'taskNumber',
            render: (_: unknown, record: PopReviewType) => record.pickupDetail?.taskNumber || '-'
        },
        ...(showReviewColumns
            ? [
                  {
                      title: t('popReview.table.disqualifyReasons'),
                      key: 'disqualifyReasons',
                      render: (_: unknown, record: PopReviewType) =>
                          record.disqualifyReasonCodes?.length
                              ? record.disqualifyReasonCodes
                                    .map(code => reasonTextByCode[code] || code)
                                    .join('; ')
                              : '-'
                  },
                  {
                      title: t('popReview.table.reviewer'),
                      key: 'reviewer',
                      render: (_: unknown, record: PopReviewType) =>
                          record.reviewer?.name || record.reviewerId || '-'
                  },
                  {
                      title: t('popReview.table.reviewedAt'),
                      key: 'reviewedAt',
                      render: (_: unknown, record: PopReviewType) =>
                          formatEpochToDateTime(record.reviewedAt)
                  }
              ]
            : []),
        {
            // 「提交时间」取 review_task.submitted_at，与筛选项「提交时间」同源
            title: t('popReview.table.submittedAt'),
            key: 'submittedAt',
            render: (_: unknown, record: PopReviewType) =>
                formatEpochToDateTime(record.submittedAt)
        },
        {
            // 「更新时间」取 pop_review.updated_at，与列表排序字段同源
            title: t('popReview.table.updatedAt'),
            key: 'updatedAt',
            render: (_: unknown, record: PopReviewType) =>
                formatEpochToDateTime(record.updatedAt)
        }
    ];

    // 操作列始终存在：已审核单不可再审核，但必须能查看详情（照片 / 日志 / 各轮原因）。
    // 按钮文案依「行的实际状态」而非当前 Tab —— 用户可同时用 statuses 筛选，两者未必对应。
    columns.push({
        title: t('popReview.table.action'),
        key: 'action',
        fixed: 'right' as const,
        render: (_: unknown, record: PopReviewType) => (
            <Button type="link" style={{padding: 0}} onClick={() => showDetail(record)}>
                {isPopReviewFinished(record.status)
                    ? t('popReview.table.view')
                    : t('popReview.table.review')}
            </Button>
        )
    });

    return (
        <Table<PopReviewType>
            rowKey="reviewTaskId"
            columns={columns}
            dataSource={popReviewData}
            loading={loading}
            pagination={false}
            // max-content + 表头不折行：列宽由内容决定，横向溢出时滚动。
            // 不写死 width 才能让「实际包裹数」这类较长表头保持一行（对齐 whiteSpace 设置）
            scroll={{x: 'max-content'}}
            className="[&_.ant-table-thead_th]:whitespace-nowrap [&_.ant-table-tbody_td]:whitespace-nowrap"
            rowSelection={
                showRowSelection
                    ? {
                          selectedRowKeys,
                          onChange: onSelectChange
                      }
                    : undefined
            }
        />
    );
};

export default PopReviewTable;
